import React, { useMemo } from 'react';
import { defineMessages, injectIntl, IntlShape } from 'react-intl';
import {
  COMMON_COLORS,
  Legend,
  SleepApneaTrendingOverviewChart as UiSleepApneaTrendingOverviewChart,
} from '@neteera/neteera-web-nextgen';

import { deepEqual } from 'src/utils/comparators';
import { PropsFromRedux } from './Connector';
import { tooltipFormatter } from './utils';
import type { ValueType } from 'recharts/types/component/DefaultTooltipContent';

enum ChartDataType {
  BAR = 'bar',
  BAR_LEFT = 'bar-left',
}

type Props = {
  intl: IntlShape;
  sleepData: PropsFromRedux['sleepData'];
  sleepDataTimeFrame: string | undefined;
  ahiScoreThreshold: number;
};

const SleepApneaTrendingOverviewChart = ({
  intl,
  sleepData,
  sleepDataTimeFrame,
  ahiScoreThreshold,
}: Props) => {
  const chartConfig = useMemo(
    () => ({
      sleep: {
        label: intl.formatMessage(messages.sleep),
        color: COMMON_COLORS.greenLight,
      },
      remSleep: {
        label: intl.formatMessage(messages.remSleep),
        color: COMMON_COLORS.greenDark,
      },
      lowAhi: {
        label: intl.formatMessage(messages.ahiBelow, { ahiScoreThreshold }),
        color: COMMON_COLORS.blueLight as string,
      },
      highAhi: {
        label: intl.formatMessage(messages.ahiAbove, { ahiScoreThreshold }),
        color: COMMON_COLORS.blueMid as string,
      },
      noAhi: {
        label: intl.formatMessage(messages.noEnoughData),
        color: COMMON_COLORS.blueMid as string,
      },
      noRemSleep: {
        label: intl.formatMessage(messages.remSleep),
        color: COMMON_COLORS.greenDark,
        unit: intl.formatMessage(messages.noEnoughData),
      },
      noSleep: {
        label: intl.formatMessage(messages.sleep),
        color: COMMON_COLORS.greenLight,
        unit: intl.formatMessage(messages.noEnoughData),
      },
    }),
    [intl, ahiScoreThreshold],
  );

  const legendConfig = useMemo(
    () => [
      {
        dataKey: 'sleep',
        label: chartConfig.sleep.label,
        color: chartConfig.sleep.color,
        type: ChartDataType.BAR_LEFT,
      },
      {
        dataKey: 'remSleep',
        label: chartConfig.remSleep.label,
        color: chartConfig.remSleep.color,
        type: ChartDataType.BAR_LEFT,
      },
      {
        dataKey: 'lowAhi',
        label: chartConfig.lowAhi.label,
        color: chartConfig.lowAhi.color,
        type: ChartDataType.BAR,
      },
      {
        dataKey: 'highAhi',
        label: chartConfig.highAhi.label,
        color: chartConfig.highAhi.color,
        type: ChartDataType.BAR,
      },
    ],
    [chartConfig],
  );

  const headerContent = sleepDataTimeFrame ? (
    <div className="flex flex-1 justify-evenly">
      <span className="text-xs text-neteera-teal-500">
        {sleepDataTimeFrame}
      </span>
    </div>
  ) : (
    <></>
  );
  return sleepData ? (
    <>
      <UiSleepApneaTrendingOverviewChart
        title={intl.formatMessage(messages.trendingOverview)}
        rightHeaderContent={headerContent}
        data={sleepData}
        config={chartConfig}
        ahiScoreThreshold={ahiScoreThreshold}
        rightAxisLabel={intl.formatMessage(messages.rightAxisLabel)}
        leftAxisLabel={intl.formatMessage(messages.leftAxisLabel)}
        tooltipFormatter={(value: ValueType | undefined) =>
          tooltipFormatter(value, intl)
        }
      />
      <Legend config={legendConfig} className="pt-0 px-0 justify-center" />
    </>
  ) : (
    <>{intl.formatMessage(messages.noData)}</>
  );
};

const messages = defineMessages({
  trendingOverview: {
    id: 'SleepApnea.TrendingOverviewChart.trendingOverview',
    defaultMessage: 'Sleep Apnea Trending overview',
  },
  sleep: {
    id: 'SleepApnea.TrendingOverviewChart.sleep',
    defaultMessage: 'Sleep',
  },
  remSleep: {
    id: 'SleepApnea.TrendingOverviewChart.remSleep',
    defaultMessage: 'REM Sleep',
  },
  ahi: {
    id: 'SleepApnea.TrendingOverviewChart.ahi',
    defaultMessage: 'AHI',
  },
  ahiBelow: {
    id: 'SleepApnea.TrendingOverviewChart.ahiBelow',
    defaultMessage: 'AHI Below {ahiScoreThreshold}',
  },
  ahiAbove: {
    id: 'SleepApnea.TrendingOverviewChart.ahiAbove',
    defaultMessage: 'AHI Above {ahiScoreThreshold}',
  },
  hoursUnit: {
    id: 'SleepApnea.TrendingOverviewChart.hoursUnit',
    defaultMessage: 'hours',
  },
  leftAxisLabel: {
    id: 'SleepApnea.TrendingOverviewChart.leftAxisLabel',
    defaultMessage: 'Hours',
  },
  rightAxisLabel: {
    id: 'SleepApnea.TrendingOverviewChart.rightAxisLabel',
    defaultMessage: 'AHI',
  },
  noData: {
    id: 'SleepApnea.TrendingOverviewChart.noData',
    defaultMessage: 'No data',
  },
  noEnoughData: {
    id: 'SleepApnea.TrendingOverviewChart.noEnoughData',
    defaultMessage: 'Not Enough Data',
  },
});

export default injectIntl(
  React.memo(SleepApneaTrendingOverviewChart, (oldProps, newProps) =>
    deepEqual(oldProps, newProps),
  ),
);
